import { z } from 'zod';

/**
 * LLM proxy schemas · per ADR-A2 (Atelier ADR · multi-provider) + slot LLM_PROXY brief.
 *
 * Single endpoint surface к client SDK: `POST /api/llm/complete`.
 * Provider routing per request payload · falls к cookie `llm_default_provider`
 * · falls к hardcoded `'claude'` (per OQ-1=G + ADR-A2 «Anthropic Claude default · matches course brand»).
 *
 * Streaming · `stream: false` only Phase 3 · `stream: true` returns HTTP 501 with
 * `code = 'streamingNotImplemented'` per OQ-3=B (forward-compat · zero impl cost).
 *
 * Privacy · raw prompts NEVER persisted · `prompt_logs` table stores only hash:length tag
 * (per OQ-4=G · `${sha256(systemPrompt+userPrompt)}:${tokenCount}`).
 */

/* ─── Provider registry ───────────────────────────────────────────────────── */

/** 4 providers per OQ-0=C · 2026-05-04 supersede ADR-A2 Option A (Gemini stays · Yandex added). */
export const LLM_PROVIDERS = ['claude', 'openai', 'gemini', 'yandex'] as const;

export const LlmProviderSchema = z.enum(LLM_PROVIDERS);

export type LlmProvider = z.infer<typeof LlmProviderSchema>;

/** Default provider · used when request omits `provider` AND cookie absent. */
export const DEFAULT_LLM_PROVIDER: LlmProvider = 'claude';

/** Cookie name carrying user-selected default provider (per OQ-1=G · client owns · zero DB touch). */
export const LLM_DEFAULT_PROVIDER_COOKIE = 'llm_default_provider';

/* ─── Request / response shapes ───────────────────────────────────────────── */

/**
 * `POST /api/llm/complete` request body.
 * `userPrompt` required · `systemPrompt` optional (provider-specific framing).
 * `model` optional · adapter picks provider default if omitted.
 * `maxTokens` capped at 4096 Phase 3 (cost guard · raise Phase 5+ если scenario нужен).
 * `temperature` 0..2 (OpenAI/Gemini) · adapters clamp если provider range ýже.
 * `stream` reserved Phase 5 · Phase 3 returns 501 если true.
 */
export const LlmCompleteRequestSchema = z.object({
  provider: LlmProviderSchema.optional(),
  model: z.string().min(1).max(100).optional(),
  systemPrompt: z.string().max(10_000).optional(),
  userPrompt: z.string().min(1).max(20_000),
  maxTokens: z.number().int().min(1).max(4096).optional(),
  temperature: z.number().min(0).max(2).optional(),
  stream: z.boolean().optional().default(false),
});

export type LlmCompleteRequest = z.infer<typeof LlmCompleteRequestSchema>;

/**
 * `POST /api/llm/complete` success response (HTTP 200).
 * `tokensIn/Out` per provider best-effort · adapters fall к 0 if SDK omits.
 * `latencyMs` measured backend-side от adapter dispatch к response receive.
 * `requestId` UUID · matches `prompt_logs.id` для observability.
 */
export const LlmCompleteResponseSchema = z.object({
  text: z.string(),
  provider: LlmProviderSchema,
  model: z.string(),
  tokensIn: z.number().int().min(0),
  tokensOut: z.number().int().min(0),
  latencyMs: z.number().int().min(0),
  requestId: z.string().uuid(),
});

export type LlmCompleteResponse = z.infer<typeof LlmCompleteResponseSchema>;

/* ─── Error codes (per ADR-001 envelope) ──────────────────────────────────── */

/**
 * LLM-specific error codes · added к ApiErrorSchema `code` field surface.
 * - `streamingNotImplemented` · 501 · per OQ-3=B
 * - `providerUnavailable` · 502 · provider downtime / 5xx upstream
 * - `providerInvalidKey` · 500 · API-key misconfigured (audit-logged)
 * - `providerRateLimit` · 429 · upstream provider rate limit (distinct от backend rate limit)
 * - `unknownProvider` · 400 · request `provider` not in enum (caught by Zod normally)
 * - `llmRateLimit` · 429 · backend rate limit hit (per OQ-2=G burst или sustained bucket)
 */
export const LLM_ERROR_CODES = [
  'streamingNotImplemented',
  'providerUnavailable',
  'providerInvalidKey',
  'providerRateLimit',
  'unknownProvider',
  'llmRateLimit',
] as const;

export type LlmErrorCode = (typeof LLM_ERROR_CODES)[number];
